import DocumentPage from './DocumentPage';

export const attendanceDocument = {
  slug: 'attendance',
  route: '/attendance',
  title: 'Attendance',
  category: 'Academics',
  format: 'DOCX',
  description:
    'EduX provides attendance registers, daily attendance sheets, workflow-based attendance capture, and detailed attendance lines for each student.',
  metrics: [
    { label: 'Primary focus', value: 'Daily attendance' },
    { label: 'Core records', value: 'Registers and sheets' },
    { label: 'Tracking model', value: 'Kiosk-ready workflow' },
  ],
  workflow: [
    'Create an attendance register for the required course and batch.',
    'Open an attendance sheet for the date, review the students, and start attendance in the kiosk workflow.',
    'Mark attendance as taken and review attendance lines and attendance types for detailed tracking.',
  ],
  sections: [
    {
      id: 'attendance-register',
      title: 'Attendance Register',
      body: [
        'EduX provides the Attendance maintenance facility for an institute. To maintain attendance, an attendance register is created for a particular batch and course.',
        'Go to Attendances -> Attendance -> Registers.',
        'The attendance register stores the core academic grouping that later connects each attendance sheet to the right course and batch.',
      ],
      figure: {
        src: '/assets/doc-images/attendance/image.png',
        alt: 'Attendance register form with name code course and batch',
        caption: 'Attendance Register form showing the linked course, batch, name, and code.',
      },
      detailCards: [
        {
          title: 'Details of Attendance Register Form',
          bullets: [
            'Name: set the name of the attendance register.',
            'Code: set the code of the attendance register.',
            'Course: select the course for which the attendance register is created.',
            'Batch: select the batch for which the attendance register is created.',
          ],
        },
      ],
    },
    {
      id: 'attendance-sheet',
      title: 'Attendance Sheet',
      body: [
        'Attendance sheet is created as per particular date and maintains attendance for that day.',
        'Go to Attendances -> Attendance -> Sheets and click on the New button to create a new attendance sheet.',
        'The attendance sheet connects the register, date, session, and student entries into a single daily attendance record.',
      ],
      figure: {
        src: '/assets/doc-images/attendance/image2.png',
        alt: 'Attendance sheet form with workflow buttons and student attendance rows',
        caption: 'Attendance Sheet form showing register details, date, workflow states, and student attendance rows.',
      },
      detailCards: [
        {
          title: 'Details of Attendance Sheet Form',
          bullets: [
            'Register: create or select the attendance register from the created attendance registers.',
            'Name: set the name of the attendance sheet.',
            'Session: select the session.',
            'Date: set the date of the attendance sheet.',
            'Student(s): select the list of absent students from the list.',
          ],
        },
      ],
      subsections: [
        {
          id: 'attendance-sheet-workflow',
          title: 'Workflow of Attendance Sheet',
          body: [
            'When an attendance sheet is created it is in Draft state.',
            'Click on the Attendance Start button to display the attendance sheet in kiosk mode for the student to sign in. It changes the state from Draft to Attendance Start.',
            'Click on Attendance Taken if the attendance is taken for that particular attendance sheet and then it will not be displayed in kiosk mode. It changes the state from Attendance Start to Attendance Taken.',
          ],
          steps: [
            'Create the attendance sheet in Draft state.',
            'Click Attendance Start to move the sheet into kiosk-ready attendance capture.',
            'Allow students to sign in on the attendance sheet.',
            'Click Attendance Taken to complete the attendance process for that day.',
          ],
        },
      ],
    },
    {
      id: 'attendance-lines',
      title: 'Attendance Lines',
      body: [
        'Go to Attendances -> Attendance -> Attendance Lines.',
        'Attendance Lines are the detailed records of attendance for each individual student.',
        'This view helps users review detailed student-level attendance entries across sheets, dates, courses, and batches.',
      ],
      figure: {
        src: '/assets/doc-images/attendance/image5.png',
        alt: 'Attendance Lines list showing student level attendance details',
        caption: 'Attendance Lines provide a detailed student-by-student record of presence, absence, lateness, and date.',
      },
    },
    {
      id: 'attendance-type',
      title: 'Attendance Type',
      body: [
        'Go to Attendances -> Configuration -> Attendance Type.',
        'Attendance Type is used to define how a student should be marked during attendance capture.',
        'The Name field stores the type name used inside attendance workflows.',
      ],
      gallery: [
        {
          src: '/assets/doc-images/attendance/image3.png',
          alt: 'Attendance Type form for a Regular Class entry',
          caption: 'Attendance Type defines the marking behavior for a class or attendance activity.',
        },
        {
          src: '/assets/doc-images/attendance/image4.png',
          alt: 'Attendance Type form showing present absent excused and late options',
          caption: 'Attendance Type can be configured to mark students as present, absent, excused, or late.',
        },
      ],
      bullets: [
        'Regular Class',
        'Exam',
        'Workshop',
        'Seminar',
      ],
    },
  ],
  related: ['timetable', 'student', 'exam'],
};

export default function AttendancePage() {
  return <DocumentPage document={attendanceDocument} />;
}
